import React, { type FC } from "react";
import { useDivisionFilterSeed } from "@app/session-defaults/useDivisionFilterSeed";
import { BoType } from "@src/api/bo";
import { DivisionType } from "@src/api/dictionaries/divisions";
import { BoStateSelect, DivisionSearchSelect, DivisionSectionSearchSelect } from "@src/components/business-data-entry";
import { BusinessForm } from "@src/components/business-form";
import { Input } from "@shared/ui/input";
import { FilterFormPanel } from "@shared/ui/form-panel";
import type { FinishedGoodsReceiptFilter } from "../../../../../../api/finishedGoodsReceipts";
import type { ListFilterPanelProps } from "../../../../../../shared/listFilterPanel";
import {
    stockFinishedGoodsReceiptFilterFormConfig,
    type StockFinishedGoodsReceiptFilterFormValues,
} from "./stockFinishedGoodsReceiptFilter.form-config";

/** Пропсы компонента {@link StockFinishedGoodsReceiptFilterForm}. */
export type StockFinishedGoodsReceiptFilterFormProps = ListFilterPanelProps<FinishedGoodsReceiptFilter>;

/** Форма фильтра списка ВЗН УП прихода на СГП. */
export const StockFinishedGoodsReceiptFilterForm: FC<StockFinishedGoodsReceiptFilterFormProps> = ({
    initialFilter,
    onApply,
}) => {
    const seed = useDivisionFilterSeed<StockFinishedGoodsReceiptFilterFormValues>("receiverCode", {
        filter: { Types: [DivisionType.Склад] },
    });

    return (
        <FilterFormPanel
            initialFilter={initialFilter}
            seed={seed}
            submissionMemory={{
                key: "stock.finishedGoodsReceipt.filter",
                schemaVersion: 2,
                fields: ["receiverCode", "senderCode", "senderSectionCode", "stateCodes"],
            }}
            formProps={{ formConfig: stockFinishedGoodsReceiptFilterFormConfig }}
            toForm={(filter) => stockFinishedGoodsReceiptFilterFormConfig.toForm(filter)}
            toDomain={(values) => stockFinishedGoodsReceiptFilterFormConfig.toDomain(values)}
            onApply={onApply}
        >
            {(form) => {
                const senderCode = form.watch("senderCode");

                return (
                    <>
                        <BusinessForm.Field name="receiverCode">
                            <DivisionSearchSelect
                                {...form.field("receiverCode").getSearchSelectProps((division) => division?.Code ?? null)}
                                flt={{ Types: [DivisionType.Склад] }}
                                fullscreenTitle="Выберите Склад СГП"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="senderCode">
                            <DivisionSearchSelect
                                {...form.field("senderCode").getSearchSelectProps((division) => division?.Code ?? null)}
                                onChange={(division) => {
                                    // TODO(forms/spp-lib): cascade-clear via form-config `dependsOn` (multi-field masters)
                                    const nextSenderCode = division?.Code ?? null;

                                    form.setValue("senderCode", nextSenderCode);
                                    if (nextSenderCode !== senderCode) {
                                        form.setValue("senderSectionCode", null);
                                    }
                                }}
                                fullscreenTitle="Выберите Цех-отправитель"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="senderSectionCode">
                            <DivisionSectionSearchSelect
                                {...form
                                    .field("senderSectionCode")
                                    .getSearchSelectProps((section) => section?.SectionCode ?? null)}
                                divisionCode={senderCode}
                                fullscreenTitle="Выберите Участок-отправитель"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="num">
                            <Input {...form.field("num").getPropsWithLabel()} />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="stateCodes">
                            <BoStateSelect
                                {...form.field("stateCodes").getControlledPropsWithLabel()}
                                boType={BoType["Внутризаводская накладная УП"]}
                                multiple
                                clearable
                                fullscreenTitle="Выберите статус(ы) ВЗН УП"
                            />
                        </BusinessForm.Field>
                    </>
                );
            }}
        </FilterFormPanel>
    );
};
